import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Truck, MapPin } from "lucide-react";
import { US_STATES } from "@/lib/states";

/**
 * ShippingOriginPanel — admin config for the from-address + default parcel
 * dimensions used by the Shippo rate quote. Both the origin AND a Shippo
 * API key (rotated below or set via SHIPPO_API_KEY) must be set for
 * at-checkout rates to work. Backend: GET/PUT /api/admin/shipping/origin.
 */
export default function ShippingOriginPanel() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({
    name: "Bulk Buy Kits",
    street1: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    default_weight_oz: 16,
    default_length_in: 10,
    default_width_in: 8,
    default_height_in: 4,
    api_key: "", // write-only — blank means "leave existing key alone"
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/shipping/origin");
      setState(data);
      setForm({
        name: data.name || "Bulk Buy Kits",
        street1: data.street1 || "",
        city: data.city || "",
        state: data.state || "",
        zip: data.zip || "",
        phone: data.phone || "",
        default_weight_oz: data.default_weight_oz ?? 16,
        default_length_in: data.default_length_in ?? 10,
        default_width_in: data.default_width_in ?? 8,
        default_height_in: data.default_height_in ?? 4,
        api_key: "", // never prefill — user pastes to rotate
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't load shipping origin");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      // Only send api_key if the operator typed a new one — omitting it
      // (or sending "") tells the backend to keep the existing stored key.
      const payload = { ...form };
      if (!payload.api_key) delete payload.api_key;
      const { data } = await api.put("/admin/shipping/origin", payload);
      setState(data);
      // Wipe the api_key field so the last4 hint below shows the just-saved key
      setForm((f) => ({ ...f, api_key: "" }));
      toast.success("Shipping origin saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-2 w-full py-2 px-3 border border-borderSubtle bg-white focus-visible:outline-none focus-visible:border-oxblood rounded text-[15px]";

  return (
    <div className="max-w-2xl mb-12" data-testid="shipping-origin-section">
      <h3 className="font-serif text-2xl mb-2 flex items-center gap-2">
        <Truck size={20} strokeWidth={1.7} className="text-oxblood" />
        Shipping origin
      </h3>
      <p className="text-sm text-charcoal/85 leading-relaxed mb-4 max-w-xl">
        Where your packages leave from. Used by the real-time USPS + UPS rate quote at checkout (Shippo). Save this AND paste your <span className="font-mono bg-charcoal/5 px-1.5 py-0.5 rounded text-[13px]">Shippo API key</span> below to enable live shipping rates.
      </p>

      {/* Status pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        <span
          className={`inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full ${
            state?.api_key_configured
              ? "text-emerald-700 bg-emerald-50 border border-emerald-200"
              : "text-oxblood bg-oxblood/10 border border-oxblood/30"
          }`}
          data-testid="shipping-api-key-status"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${state?.api_key_configured ? "bg-emerald-600" : "bg-oxblood"}`} />
          API Key {state?.api_key_configured ? `set · …${state.api_key_last4}` : "missing"}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full ${
            state?.configured
              ? "text-emerald-700 bg-emerald-50 border border-emerald-200"
              : "text-oxblood bg-oxblood/10 border border-oxblood/30"
          }`}
          data-testid="shipping-origin-status"
        >
          <MapPin size={11} />
          Origin {state?.configured ? "configured" : "incomplete"}
        </span>
      </div>

      <form onSubmit={save} className="border border-borderSubtle bg-white p-6 rounded-2xl space-y-5" data-testid="shipping-origin-form">
        <div>
          <label className="overline font-bold text-charcoal">Company / from name</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="shipping-name-input" />
        </div>
        <div>
          <label className="overline font-bold text-charcoal">Street address</label>
          <input type="text" value={form.street1} onChange={(e) => setForm({ ...form, street1: e.target.value })} className={inputCls} required data-testid="shipping-street-input" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <label className="overline font-bold text-charcoal">City</label>
            <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} required data-testid="shipping-city-input" />
          </div>
          <div>
            <label className="overline font-bold text-charcoal">State</label>
            <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls + " appearance-none"} required data-testid="shipping-state-input">
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="overline font-bold text-charcoal">ZIP</label>
            <input type="text" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className={inputCls} required data-testid="shipping-zip-input" />
          </div>
          <div>
            <label className="overline font-bold text-charcoal">Phone (optional)</label>
            <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="shipping-phone-input" />
          </div>
        </div>

        <div className="border-t border-borderSubtle pt-5">
          <p className="overline text-charcoal mb-3">Default parcel (used when a product has no ship weight set)</p>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-[13px] font-medium text-charcoal/85">Weight (oz)</label>
              <input type="number" min={1} step={0.5} value={form.default_weight_oz} onChange={(e) => setForm({ ...form, default_weight_oz: parseFloat(e.target.value) || 16 })} className={inputCls} data-testid="shipping-default-weight-input" />
            </div>
            <div>
              <label className="text-[13px] font-medium text-charcoal/85">L (in)</label>
              <input type="number" min={1} step={0.5} value={form.default_length_in} onChange={(e) => setForm({ ...form, default_length_in: parseFloat(e.target.value) || 10 })} className={inputCls} data-testid="shipping-default-length-input" />
            </div>
            <div>
              <label className="text-[13px] font-medium text-charcoal/85">W (in)</label>
              <input type="number" min={1} step={0.5} value={form.default_width_in} onChange={(e) => setForm({ ...form, default_width_in: parseFloat(e.target.value) || 8 })} className={inputCls} data-testid="shipping-default-width-input" />
            </div>
            <div>
              <label className="text-[13px] font-medium text-charcoal/85">H (in)</label>
              <input type="number" min={1} step={0.5} value={form.default_height_in} onChange={(e) => setForm({ ...form, default_height_in: parseFloat(e.target.value) || 4 })} className={inputCls} data-testid="shipping-default-height-input" />
            </div>
          </div>
        </div>

        <div className="border-t border-borderSubtle pt-5">
          <p className="overline text-charcoal mb-2">Shippo API key</p>
          <p className="text-[13px] text-charcoal/85 leading-relaxed mb-3">
            Where to get it: sign in at <a className="underline decoration-oxblood/40" href="https://apps.goshippo.com/settings/api" target="_blank" rel="noreferrer">apps.goshippo.com → Settings → API</a>. Use a <span className="font-mono">shippo_test_…</span> key while validating, then rotate to a <span className="font-mono">shippo_live_…</span> key when you're ready to charge real postage. We store it on the server and never show the full value again.
          </p>
          <input
            type="password"
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            autoComplete="off"
            placeholder={state?.api_key_configured ? `••••••••••••${(state.api_key_last4 || "").toUpperCase()} (leave blank to keep)` : "shippo_test_…"}
            className={inputCls + " font-mono"}
            data-testid="shipping-api-key-input"
          />
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2.5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-[13px] uppercase rounded-full transition-colors disabled:opacity-50"
            data-testid="shipping-origin-save-btn"
          >
            {busy ? "Saving…" : "Save shipping origin"}
          </button>
        </div>
      </form>
    </div>
  );
}
